import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList, Cell,
  Tooltip as RechartTooltip, ScatterChart, Scatter, ZAxis, ReferenceLine, ReferenceArea,
} from 'recharts';
import { PageHeader, StatCard, TierBadge, SubTabBar, fmtDollar, fmtCount } from './shared';
import { apiFetch } from '../api';

// ── Frontend-only constants ────────────────────────────────────────────────────

const OE_COLORS   = ['#003c71', '#0067a0', '#00859e', '#00a7b5', '#5bc4cf', '#9ddde7'];
const QUAD_COLORS = { 'H$/HC': '#dc3545', 'H$/LC': '#fd7e14', 'L$/HC': '#198754', 'L$/LC': '#0dcaf0' };
const QUAD_LABELS = { 'H$/HC': 'High $/High Compl.', 'H$/LC': 'High $/Low Compl.', 'L$/HC': 'Low $/High Compl.', 'L$/LC': 'Low $/Low Compl.' };

function fmtDollarParen(v) {
  if (typeof v !== 'number') return v;
  const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `($${abs})` : `$${abs}`;
}

function fmtRatio(v) {
  if (typeof v !== 'number') return v;
  return v.toFixed(2);
}

function tierLeakageColor(label) {
  return label === 'Tier 1' ? '#198754' : label === 'Tier 2' ? '#fd7e14' : '#dc3545';
}

function LoadingRow() {
  return <div className="p-4 text-muted">Loading…</div>;
}

function ErrorRow() {
  return <div className="p-4 text-danger">Failed to load dashboard data.</div>;
}

// ── Pure UI components ─────────────────────────────────────────────────────────

function TrendBar({ data }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 0.01;
  const w = 5, gap = 1, h = 26;
  const totalW = data.length * (w + gap) - gap;
  return (
    <svg width={totalW} height={h} style={{ display: 'block' }}>
      {data.map((v, i) => {
        const barH = Math.max(2, Math.round(((v - min) / range) * (h - 4)) + 2);
        return <rect key={i} x={i * (w + gap)} y={h - barH} width={w} height={barH} rx={1} fill={v > 1 ? '#dc3545' : '#198754'} />;
      })}
    </svg>
  );
}

function OEFilterBar() {
  return (
    <div className="card mb-3">
      <div className="card-body py-2">
        <div className="row g-2 align-items-end">
          {[
            ['Episode Trigger Start', ''],
            ['O/E Reporting Period', '1/1/2024 – 6/30/2025'],
            ['Min. Volume', '30'],
            ['Cost O/E Range', '0.0 – 2.0'],
            ['Complication O/E Range', 'Optional'],
          ].map(([label, val]) => (
            <div key={label} className="col-12 col-sm-6 col-md-4 col-lg-2">
              <label className="form-label mb-1" style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>{label}</label>
              <input className="form-control form-control-sm" readOnly value={val} />
            </div>
          ))}
          <div className="col-12 col-sm-6 col-md-4 col-lg-2">
            <button className="btn btn-sm btn-primary w-100">Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterMenu() {
  return (
    <div className="card" style={{ minWidth: 150 }}>
      <div className="card-header fw-semibold" style={{ fontSize: '0.78rem' }}>Filter Menu</div>
      <div className="list-group list-group-flush">
        {['Episode Name', 'Reporting LOB', 'State', 'Region', 'Network Adequacy'].map(label => (
          <button key={label} className="list-group-item list-group-item-action py-2"
            style={{ fontSize: '0.8rem' }}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function VolumeBarChart({ data, label }) {
  return (
    <div className="card">
      <div className="card-header fw-semibold">{label} Ranked by Episode Volume</div>
      <div className="card-body">
        <div className="d-flex gap-3 mb-2" style={{ fontSize: '0.75rem' }}>
          <span><span style={{ color: '#dc3545' }}>■</span> Savings</span>
          <span><span style={{ color: '#adb5bd' }}>■</span> No Savings</span>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 55 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={120} />
            <RechartTooltip formatter={v => [v, 'Episodes']} />
            <Bar dataKey="vol" radius={[0, 3, 3, 0]}>
              <LabelList dataKey="vol" position="right" style={{ fontSize: 10 }} />
              {data.map((d, i) => (
                <Cell key={i} fill={d.savings ? '#dc3545' : '#adb5bd'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function QuadrantGrid({ rows }) {
  const pts = rows.slice(1).map(r => ({
    name: r.name,
    x:    parseFloat(r.oe),
    y:    r.complOE === '—' ? (r.quad.includes('HC') ? 1.15 : 0.90) : parseFloat(r.complOE),
    z:    r.eps,
    quad: r.quad,
  }));
  const BubbleTip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 4, padding: '6px 10px', fontSize: '0.75rem' }}>
        <div className="fw-semibold mb-1">{d.name}</div>
        <div>Cost O/E: <strong>{d.x.toFixed(2)}</strong></div>
        <div>Complic. O/E: <strong>{d.y.toFixed(2)}</strong></div>
        <div>Episodes: <strong>{d.z.toLocaleString()}</strong></div>
      </div>
    );
  };
  return (
    <div className="card">
      <div className="card-header fw-semibold">O/E Ratio & Quality Matrix</div>
      <div className="card-body">
        <div className="d-flex flex-wrap gap-3 mb-2" style={{ fontSize: '0.72rem' }}>
          {Object.entries(QUAD_LABELS).map(([q, label]) => (
            <span key={q}><span style={{ color: QUAD_COLORS[q] }}>■</span> {label}</span>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={210}>
          <ScatterChart margin={{ top: 5, right: 20, bottom: 30, left: 10 }}>
            <ReferenceArea x1={1.10} x2={1.0} y1={1.0} y2={1.50} fill="#fde8e8" fillOpacity={0.3} />
            <ReferenceArea x1={1.10} x2={1.0} y1={0.7} y2={1.0}  fill="#fff3cd" fillOpacity={0.3} />
            <ReferenceArea x1={1.0}  x2={0.8} y1={1.0} y2={1.50} fill="#e8f5e9" fillOpacity={0.3} />
            <ReferenceArea x1={1.0}  x2={0.8} y1={0.7} y2={1.0}  fill="#d1ecf1" fillOpacity={0.3} />
            <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
            <XAxis type="number" dataKey="x" name="Cost O/E"    domain={[0.80, 1.10]} reversed tick={{ fontSize: 10 }} label={{ value: 'Cost O/E', position: 'insideBottom', offset: -15, fontSize: 10 }} />
            <YAxis type="number" dataKey="y" name="Complic. O/E" domain={[0.75, 1.40]} tick={{ fontSize: 10 }} label={{ value: 'Complic. O/E', angle: -90, position: 'insideLeft', offset: 10, fontSize: 10 }} width={50} />
            <ZAxis type="number" dataKey="z" range={[40, 500]} name="Episodes" />
            <RechartTooltip content={<BubbleTip />} />
            {Object.entries(QUAD_COLORS).map(([quad, color]) => (
              <Scatter key={quad} name={QUAD_LABELS[quad]} data={pts.filter(p => p.quad === quad)} fill={color} fillOpacity={0.8} />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function OETierLists({ topLabel, topItems, botItems }) {
  return (
    <div className="row g-3 mt-0">
      <div className="col-12 col-md-6">
        <div className="card mt-3">
          <div className="card-header fw-semibold" style={{ background: '#198754', color: '#fff' }}>Top N Tier 1 {topLabel}</div>
          <div className="card-body p-0">
            {topItems.map(t => <div key={t} className="px-3 py-2 border-bottom" style={{ fontSize: '0.82rem' }}>✓ {t}</div>)}
          </div>
        </div>
      </div>
      <div className="col-12 col-md-6">
        <div className="card mt-3">
          <div className="card-header fw-semibold" style={{ background: '#dc3545', color: '#fff' }}>Bottom N Tier 4 {topLabel}</div>
          <div className="card-body p-0">
            {botItems.map(t => <div key={t} className="px-3 py-2 border-bottom" style={{ fontSize: '0.82rem' }}>⚠ {t}</div>)}
          </div>
        </div>
      </div>
    </div>
  );
}

function OEComparisonTable({ rows, entityLabel }) {
  return (
    <div className="card">
      <div className="card-header fw-semibold">O/E Ratio Applied for {entityLabel} Normalization</div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-sm mb-0 dash-table" style={{ fontSize: '0.75rem' }}>
            <thead>
              <tr>
                <th>{entityLabel} Name</th><th>Quad</th><th>Eps</th><th>Total Allowed</th>
                <th>Avg Allowed</th><th>Cost O/E</th><th>Savings Oppty</th>
                <th>Post-Acute</th><th>Complic. O/E</th><th>% Complic.</th>
                <th>Readmit O/E</th><th>Post Win. ED</th><th>Any ED O/E</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={i === 0 ? { background: '#f0f4f8', fontWeight: 600 } : {}}>
                  <td className="fw-semibold">{r.name}</td>
                  <td>{r.quad}</td><td>{fmtCount(r.eps)}</td><td>{fmtDollar(r.total)}</td><td>{fmtDollar(r.avg)}</td>
                  <td className={r.oe > 1 ? 'text-danger fw-semibold' : 'text-success fw-semibold'}>{fmtRatio(r.oe)}</td>
                  <td className={r.savings < 0 ? 'text-success' : 'text-danger'}>{fmtDollarParen(r.savings)}</td>
                  <td>{fmtDollarParen(r.postAcute)}</td>
                  <td className={r.complOE !== '—' && r.complOE > 1 ? 'text-danger' : ''}>{fmtRatio(r.complOE)}</td>
                  <td>{typeof r.pctComplic === 'number' ? `${r.pctComplic}%` : r.pctComplic}</td>
                  <td className={r.readmit !== '—' && r.readmit > 1 ? 'text-danger' : ''}>{fmtRatio(r.readmit)}</td>
                  <td>{fmtRatio(r.postEd)}</td><td>{fmtRatio(r.anyEd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function OEIndexTable({ rows, entityLabel }) {
  return (
    <div className="card">
      <div className="card-header fw-semibold">O/E Index Window — {entityLabel} Phase Breakdown</div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-sm mb-0 dash-table" style={{ fontSize: '0.75rem' }}>
            <thead>
              <tr>
                <th>{entityLabel}</th><th>Eps</th>
                <th>Pre-Trigger $</th><th>Pre O/E</th><th>Pre Peer $</th>
                <th>Trigger $</th><th>Trig O/E</th><th>Trig Peer $</th>
                <th>Post-Trigger $</th><th>Post O/E</th><th>Post Peer $</th>
                <th>Rx $</th><th>Rx O/E</th><th>Rx Peer $</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={i === 0 ? { background: '#f0f4f8', fontWeight: 600 } : {}}>
                  <td className="fw-semibold">{r.name}</td><td>{fmtCount(r.eps)}</td>
                  <td>{fmtDollar(r.preTrig)}</td>
                  <td className={r.preOE > 1 ? 'text-danger fw-semibold' : 'text-success fw-semibold'}>{fmtRatio(r.preOE)}</td>
                  <td className={r.prePeer < 0 ? 'text-success' : 'text-danger'}>{fmtDollarParen(r.prePeer)}</td>
                  <td>{fmtDollar(r.trig)}</td>
                  <td className={r.trigOE > 1 ? 'text-danger fw-semibold' : 'text-success fw-semibold'}>{fmtRatio(r.trigOE)}</td>
                  <td className={r.trigPeer < 0 ? 'text-success' : 'text-danger'}>{fmtDollarParen(r.trigPeer)}</td>
                  <td>{fmtDollar(r.post)}</td>
                  <td className={r.postOE > 1 ? 'text-danger fw-semibold' : 'text-success fw-semibold'}>{fmtRatio(r.postOE)}</td>
                  <td className={r.postPeer < 0 ? 'text-success' : 'text-danger'}>{fmtDollarParen(r.postPeer)}</td>
                  <td>{fmtDollar(r.rx)}</td>
                  <td className={r.rxOE > 1 ? 'text-danger fw-semibold' : 'text-success fw-semibold'}>{fmtRatio(r.rxOE)}</td>
                  <td className={r.rxPeer < 0 ? 'text-success' : 'text-danger'}>{fmtDollarParen(r.rxPeer)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Views ─────────────────────────────────────────────────────────────────────

function OEDashboardView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/provider-intelligence/oe-dash')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Provider Intelligence  ·  Procedural O/E Opportunity Analysis Dashboard" memberCount={memberCount} />
      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>
      <div className="row g-3">
        <div className="col-12 col-xl-5">
          <div className="card">
            <div className="card-header fw-semibold">Top Episodes by Savings Opportunity ($M)</div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.oeEpisodes} layout="vertical" margin={{ left: 10, right: 55 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `$${v}M`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                  <RechartTooltip formatter={v => [`$${v}M`, 'Savings']} />
                  <Bar dataKey="savings" radius={[0, 3, 3, 0]}>
                    {data.oeEpisodes.map((_, i) => <Cell key={i} fill={OE_COLORS[i]} />)}
                    <LabelList dataKey="savings" position="right" formatter={v => `$${v}M`} style={{ fontSize: 10 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-7">
          <div className="card">
            <div className="card-header fw-semibold">Savings Opportunity by Episode Type</div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0 dash-table">
                  <thead>
                    <tr><th>Episode</th><th>Episodes Above Exp.</th><th>Savings/Episode</th><th>Total Savings Oppty</th><th>Avg Allowed</th><th>% Complic.</th></tr>
                  </thead>
                  <tbody>
                    {data.oeDashboardTable.map(r => (
                      <tr key={r.episode}>
                        <td className="fw-semibold" style={{ fontSize: '0.8rem' }}>{r.episode}</td>
                        <td>{fmtCount(r.above)}</td><td>{fmtDollar(r.savings)}</td>
                        <td className="text-success fw-semibold">{fmtDollar(r.total)}</td>
                        <td>{fmtDollar(r.avg)}</td><td>{r.complic}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ScorecardView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/provider-intelligence/scorecard')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Provider Intelligence  ·  Scorecard & Benchmarking" memberCount={memberCount} />
      <div className="dash-stats-row">
        {data.kpis.map((k, i) => <StatCard key={i} {...k} />)}
      </div>
      <div className="card">
        <div className="card-header fw-semibold">Cardiology Providers — Performance Scorecard</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-sm mb-0 dash-table">
              <thead>
                <tr><th>Provider / Group</th><th>Quality O/E</th><th>Cost O/E</th><th>Util O/E</th><th>Referral</th><th>Leakage %</th><th>Ep. PMPM</th><th>VBC Tier</th><th>Steerage %</th><th>12-mo Trend</th></tr>
              </thead>
              <tbody>
                {data.providerScorecard.map(r => (
                  <tr key={r.provider}>
                    <td className="fw-semibold">{r.provider}</td>
                    <td className={r.quality > 1 ? 'text-danger' : 'text-success'}>{r.quality}</td>
                    <td className={r.cost    > 1 ? 'text-danger' : 'text-success'}>{r.cost}</td>
                    <td className={r.util    > 1 ? 'text-danger' : 'text-success'}>{r.util}</td>
                    <td>{r.referral}</td>
                    <td className={r.leakage > 20 ? 'text-danger fw-semibold' : ''}>{r.leakage}%</td>
                    <td>{fmtDollar(r.pmpm)}</td>
                    <td><TierBadge tier={r.tier} /></td>
                    <td className={r.steerage === 'Preferred' ? 'text-success fw-semibold' : r.steerage === 'Review' ? 'text-danger fw-semibold' : ''}>{r.steerage}</td>
                    <td><TrendBar data={r.trendData} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="row g-3 mt-0">
        <div className="col-12 col-xl-6">
          <div className="card mt-3">
            <div className="card-header fw-semibold">Referral Leakage by Provider Tier</div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={data.referralLeakage} margin={{ top: 18, right: 20, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <RechartTooltip formatter={v => [v, 'Providers']} />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                    {data.referralLeakage.map((r, i) => <Cell key={i} fill={tierLeakageColor(r.label)} />)}
                    <LabelList dataKey="count" position="top" style={{ fontSize: 11, fontWeight: 600 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="card mt-3">
            <div className="card-header fw-semibold">VBC Contracting Opportunities</div>
            <div className="card-body p-0">
              {data.vbcOpportunities.map((o, i) => (
                <div key={i} className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
                  <span style={{ fontSize: '0.82rem' }}>{o.desc}</span>
                  <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                    <span style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>{o.label}</span>
                    <TierBadge tier={o.tier} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function NetworkShiftView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiFetch('/api/dashboards/provider-intelligence/network')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Provider Intelligence  ·  Network Shift Opportunity Model" memberCount={memberCount} />
      <div className="row g-3">
        <div className="col-12 col-xl-8">
          <div className="card">
            <div className="card-header fw-semibold">Provider O/E Benchmark — Cardiology</div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm mb-0 dash-table">
                  <thead>
                    <tr><th>Provider / Group</th><th>Cost O/E</th><th>Annual Episodes</th><th>Ep. PMPM</th><th>Tier</th><th>Shift Target</th></tr>
                  </thead>
                  <tbody>
                    {data.nsProviders.map(r => (
                      <tr key={r.provider}>
                        <td className="fw-semibold">{r.provider}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span className={r.oe > 1 ? 'text-danger fw-semibold' : 'text-success fw-semibold'} style={{ minWidth: 30 }}>{r.oe}</span>
                            <div style={{ width: 52, height: 6, background: '#e9ecef', borderRadius: 3, flexShrink: 0 }}>
                              <div style={{ width: `${Math.min(100, (r.oe / 1.4) * 100)}%`, height: '100%', background: r.oe > 1 ? '#dc3545' : '#198754', borderRadius: 3 }} />
                            </div>
                          </div>
                        </td>
                        <td>{fmtCount(r.episodes)}</td><td>{fmtDollar(r.pmpm)}</td>
                        <td><TierBadge tier={r.tier} /></td>
                        <td className={r.shift.startsWith('+') ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>{r.shift}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-4">
          <div className="card h-100">
            <div className="card-header fw-semibold">Financial Upside Calculator</div>
            <div className="card-body">
              <table className="table table-sm mb-3 dash-table">
                <tbody>
                  {data.financialUpside.rows.map(([k, v]) => (
                    <tr key={k}><td className="text-muted">{k}</td><td className="fw-semibold text-end">{typeof v === 'number' ? fmtDollar(v) : v}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="text-center p-2" style={{ background: '#e8f5e9', borderRadius: 8 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--eh-text-mid)' }}>Projected Annual Savings</div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#198754' }}>{fmtDollar(data.financialUpside.savings)}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--eh-text-mid)' }}>{data.financialUpside.description}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row g-3 mt-0">
        <div className="col-12 col-xl-7">
          <div className="card mt-3">
            <div className="card-header fw-semibold">Projected Savings by Shift Scenario (Annual)</div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={195}>
                <BarChart data={data.nsScenarios} margin={{ left: 10, right: 30, top: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${v}K`} />
                  <RechartTooltip formatter={v => [`$${v}K`, 'Savings']} />
                  <Bar dataKey="savings" radius={[3, 3, 0, 0]}>
                    {data.nsScenarios.map((_, i) => <Cell key={i} fill={OE_COLORS[i]} />)}
                    <LabelList dataKey="savings" position="top" formatter={v => `$${v}K`} style={{ fontSize: 10 }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-5">
          <div className="card mt-3">
            <div className="card-header fw-semibold">Additional Impact Metrics</div>
            <div className="card-body p-0">
              {data.nsImpactMetrics.map(m => (
                <div key={m.metric} className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--eh-navy)' }}>{m.metric}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--eh-text-mid)' }}>{m.sub}</div>
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#198754', flexShrink: 0, marginLeft: 8 }}>
                    {m.fmt === 'dollar' ? fmtDollar(m.value) : m.fmt === 'count' ? fmtCount(m.value) : `${m.value}${m.unit || ''}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const PG_SUBTABS = [
  { key: 'overview',   label: 'Overview'     },
  { key: 'comparison', label: 'Comparison'   },
  { key: 'index',      label: 'Index Window' },
];

function ProceduralOEProviderGroupView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [sub, setSub] = useState('overview');

  useEffect(() => {
    apiFetch('/api/dashboards/provider-intelligence/pg')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Provider Intelligence  ·  Procedural O/E — Provider Group" memberCount={memberCount} />
      <SubTabBar tabs={PG_SUBTABS} active={sub} onChange={setSub} />
      <OEFilterBar />
      <div className="row g-3 align-items-start">
        <div className="col-auto">
          <FilterMenu />
        </div>
        <div className="col">
          {sub === 'overview' && (
            <div className="row g-3">
              <div className="col-12 col-xl-6">
                <VolumeBarChart data={data.pgVolume} label="Provider Group" />
              </div>
              <div className="col-12 col-xl-6">
                <QuadrantGrid rows={data.pgComparison} />
              </div>
              <div className="col-12">
                <OETierLists
                  topLabel="Provider Groups"
                  topItems={data.pgTopItems}
                  botItems={data.pgBotItems}
                />
              </div>
            </div>
          )}
          {sub === 'comparison' && <OEComparisonTable rows={data.pgComparison} entityLabel="Provider Group" />}
          {sub === 'index'      && <OEIndexTable      rows={data.pgIndex}      entityLabel="Provider Group" />}
        </div>
      </div>
    </>
  );
}

const FAC_SUBTABS = [
  { key: 'overview',   label: 'Overview'     },
  { key: 'comparison', label: 'Comparison'   },
  { key: 'index',      label: 'Index Window' },
];

function ProceduralOEFacilityView({ memberCount }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [sub, setSub] = useState('overview');

  useEffect(() => {
    apiFetch('/api/dashboards/provider-intelligence/facility')
      .then(r => r.json())
      .then(setData)
      .catch(() => setError(true));
  }, []);

  if (error) return <ErrorRow />;
  if (!data)  return <LoadingRow />;

  return (
    <>
      <PageHeader title="Provider Intelligence  ·  Procedural O/E — Facility" memberCount={memberCount} />
      <SubTabBar tabs={FAC_SUBTABS} active={sub} onChange={setSub} />
      <OEFilterBar />
      <div className="row g-3 align-items-start">
        <div className="col-auto">
          <FilterMenu />
        </div>
        <div className="col">
          {sub === 'overview' && (
            <div className="row g-3">
              <div className="col-12 col-xl-6">
                <VolumeBarChart data={data.facVolume} label="Facility" />
              </div>
              <div className="col-12 col-xl-6">
                <QuadrantGrid rows={data.facComparison} />
              </div>
              <div className="col-12">
                <OETierLists
                  topLabel="Facilities"
                  topItems={data.facTopItems}
                  botItems={data.facBotItems}
                />
              </div>
            </div>
          )}
          {sub === 'comparison' && <OEComparisonTable rows={data.facComparison} entityLabel="Facility" />}
          {sub === 'index'      && <OEIndexTable      rows={data.facIndex}      entityLabel="Facility"  />}
        </div>
      </div>
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const SUBTABS = [
  { key: 'oe-dash',   label: 'O/E Dashboard'     },
  // { key: 'scorecard', label: 'Scorecard'          },
  { key: 'network',   label: 'Network Shift'      },
  { key: 'pg',        label: 'Provider Group O/E' },
  { key: 'facility',  label: 'Facility O/E'       },
];

const VIEW_COMPONENTS = {
  'oe-dash':  OEDashboardView,
  scorecard:  ScorecardView,
  network:    NetworkShiftView,
  pg:         ProceduralOEProviderGroupView,
  facility:   ProceduralOEFacilityView,
};

export default function ProviderIntelligence() {
  const [sub, setSub] = useState('oe-dash');
  const [memberCount, setMemberCount] = useState(null);
  const ViewComponent = VIEW_COMPONENTS[sub];
  useEffect(() => {
    apiFetch('/api/dashboards/intelligence-config')
      .then(r => r.json())
      .then(d => setMemberCount(d.membership_count))
      .catch(() => {});
  }, []);
  return (
    <div className="dash-tab-content">
      <SubTabBar tabs={SUBTABS} active={sub} onChange={setSub} />
      <ViewComponent memberCount={memberCount} />
    </div>
  );
}
